import Toastify from 'toastify-js';

import { apiRequest } from '@common/api/utils';
import {
  COUNTRY_CODE_SELECT_CONFIG,
} from '@event_landing/common/selectConfigs';
import { formFieldNames } from './constants';
import { FlowTypes } from '@common/vanilla_ui/general/formFlow';
import {
  Form, FormFlow, Select,
} from '@common/vanilla_ui/general';
import { isPresent } from '@common/utils/type';
import Validator from '@common/lib/validator';
import ValidateSelect from
  '@common/vanilla_ui/forms/validators/selectValidator';
import FormTracker from
  '@scaler_landing/pages/index_page/registerationForm/tracking';
import gtmTracking from '@common/vanilla_ui/tracking/gtm';
import {
  getAttribution,
  setAttribution,
} from '@common/vanilla_ui/tracking/attribution';

const formId = 'events-application-form';

const applicationFormHandlers = {
  'events-application-form-phone': '_eventsApplicationFormPhoneHandler',
  'events-application-form-otp-state':
    '_eventsApplicationFormOtpStateHandler',
};

const validatorSchema = {
  phone_number: 'mobile',
  graduation_year: {
    required: true,
    message: 'Add correct grad year',
    isValid: (value) => {
      const currentTime = new Date();
      const currentYear = currentTime.getFullYear();
      return value <= currentYear + 8 && value >= currentYear - 50;
    },
  },
  otp_verification: {
    required: true,
    message: 'Please fill all the digits',
    isValid: (value) => value.length === 6,
  },
};

const eventPage = document.querySelector('.event-page');
const formFlowEl = document.querySelector(`#${formId}`);
const formInputClass = 'form-input';
const formInputFilledClass = `${formInputClass}--filled`;
const formInputErrorClass = `${formInputClass}--error`;
const formFieldClass = 'form-field';
const fieldErrorClass = `${formFieldClass}__error`;
const otp = (formFlowEl?.getAttribute('otp') === 'true');
const otpEl = document.getElementById('otp');
const whatsappConsentInputId = `${formId}-whatsapp_consent`;
const whatsappConsentInputEl = document.getElementById(whatsappConsentInputId);

class ApplicationForm extends FormFlow {
  constructor(id, waitTime = 3000, mode = 'modal', autoInitialise = true) {
    const formType = FlowTypes.modal;
    super('events-application-form', id, formType, mode);

    this._waitTime = waitTime;
    this.userDetails = {};
    this.initializeOrgYearInputs();
    this.initializeUniversityInputs();
    this.initializeOrgnameInputs();
    this.initializePositionInputs();
    this.eventSlug = '';
    this.redirectionURL = '';
    this.validator = new Validator(validatorSchema);
    this._setFormHandlers();
    if (autoInitialise) this.landingAuthFlowInitialize();
  }

  landingAuthFlowInitialize() {
    this.initialize();
  }

  handleTriggerClick(event) {
    event.preventDefault();
    event.stopPropagation();

    const triggerEl = event.currentTarget;
    const slug = triggerEl.getAttribute('data-event-slug');
    this.eventSlug = slug;
    const mode = triggerEl.getAttribute('data-mode');
    const proceedTo = triggerEl.getAttribute('data-proceed');
    this.startFlow(mode, proceedTo);
    const intent = triggerEl.getAttribute('data-intent');
    if (intent) {
      setAttribution(intent);
    }
  }

  _initializeTriggers() {
    this._triggerEls.forEach(triggerEl => {
      triggerEl.addEventListener('click', this.handleTriggerClick.bind(this));
    });
  }

  _setFormHandlers() {
    Object.keys(applicationFormHandlers).forEach(mode => {
      this.setFormHandler(mode, this[applicationFormHandlers[mode]]);
    });
  }

  initializeOrgYearInputs = () => {
    const orgyearInputId = `${formId}-orgyear`;
    const orgSelect = new Select(orgyearInputId, { searchable: true });
    ValidateSelect.initialize({ element: orgSelect, formId });
  };

  initializeUniversityInputs = () => {
    const universityInputId = `${formId}-university`;
    const universityInputEl = document.getElementById(universityInputId);
    if (universityInputEl) {
      const uniSelect = new Select(universityInputId, {
        isAsync: true,
        isCreatable: true,
        loadOptions: async (keyword) => {
          const json = await apiRequest(
            'GET',
            '/get-universities',
            null,
            { params: { q: keyword } },
          );
          return json.items.map(item => ({ label: item.text, value: item.id }));
        },
      });
      ValidateSelect.initialize({ element: uniSelect, formId });
    }
  }

  initializeOrgnameInputs = () => {
    const orgnameInputId = `${formId}-orgname`;
    const orgnameInputEl = document.getElementById(orgnameInputId);
    if (orgnameInputEl) {
      const orgnameSelect = new Select(orgnameInputId, {
        isAsync: true,
        isCreatable: true,
        loadOptions: async (keyword) => {
          const json = await apiRequest(
            'GET',
            '/get-companies',
            null,
            { params: { q: keyword } },
          );
          return json.items.map(item => ({ label: item.text, value: item.id }));
        },
      });
      ValidateSelect.initialize({ element: orgnameSelect, formId });
    }
  }

  initializePositionInputs = () => {
    const positionInputId = `${formId}-position`;
    const otherPositionInputId = `${formId}-other-position`;
    const otherPositionFieldSelector = "[data-name='other-position']";
    const positionInputEl = document.getElementById(positionInputId);
    const otherPositionInputEl = document.getElementById(otherPositionInputId);
    const otherPositionFieldEl = formFlowEl?.querySelector(
    otherPositionFieldSelector,
  );
    if (positionInputEl) {
      const positionSelect = new Select(positionInputId, { searchable: true });
      ValidateSelect.initialize({ element: positionSelect, formId });
      positionSelect.on('change', (value) => {
        if (otherPositionFieldEl) {
          if (value.toLowerCase() === 'other') {
            otherPositionFieldEl.classList.remove('hidden');
            otherPositionInputEl.required = true;
          } else {
            otherPositionFieldEl.classList.add('hidden');
            otherPositionInputEl.required = false;
          }
        }
      });
    }
  }

  _phoneHandlerCommon(
    {
      phoneFormId,
      normalizedPhoneInputId,
      phoneNumberInputId,
      countryCodeSelect,
      formEl,
      submitBtn,
      phoneElKey,
    },
  ) {
    const proceedBtn = submitBtn;
    const normalizedPhoneEl = document.getElementById(
      normalizedPhoneInputId,
    );
    this[phoneElKey] = document.getElementById(
      phoneNumberInputId,
    );
    const phoneContainer = formEl.querySelector(
      `.${phoneFormId}__primary-phone-number`,
    );
    const errorField = phoneContainer?.querySelector(
      `.${fieldErrorClass}`,
    );

    const handlePhoneFieldsChange = () => {
      this[phoneElKey].value = `${countryCodeSelect.selectedValue}-`
      + `${normalizedPhoneEl.value}`;
      const { phone_number: error } = this.validator.check({
        phone_number: this[phoneElKey].value,
      }) || {};
      if (isPresent(error)) {
        this._toggleErrorField(
          {
            errorEle: errorField,
            inputEls: [normalizedPhoneEl],
            error,
            addInputClass: formInputErrorClass,
            removeInputClass: formInputFilledClass,
          },
        );
        if (proceedBtn) proceedBtn.disabled = true;
      } else {
        this._toggleErrorField(
          {
            errorEle: errorField,
            inputEls: [normalizedPhoneEl],
            addInputClass: formInputFilledClass,
            removeInputClass: formInputErrorClass,
          },
        );
        if (proceedBtn) proceedBtn.disabled = false;
      }
    };
    if (countryCodeSelect && normalizedPhoneEl && this[phoneElKey]) {
      normalizedPhoneEl.addEventListener('input', handlePhoneFieldsChange);
      countryCodeSelect.on('change', handlePhoneFieldsChange);
    }
  }

  initializePhoneHandler(formEl, submitBtn = null) {
    const phoneFormId = `${formId}`;
    const normalizedPhoneInputId = `${formId}__phone-number`;
    const phoneCountryCodeInputId = `${formId}-phone-country-code`;
    const phoneNumberInputId = `${formId}__phone`;
    const countryCodeSelect = new Select(
      phoneCountryCodeInputId,
      COUNTRY_CODE_SELECT_CONFIG,
    );
    this._phoneHandlerCommon({
      phoneFormId,
      countryCodeSelect,
      normalizedPhoneInputId,
      phoneNumberInputId,
      formEl,
      submitBtn,
      phoneElKey: 'phoneNumberEl',
    });
  }

  initializeOtpPhoneHandler(formEl, submitBtn = null) {
    const phoneFormId = `${formId}`;
    const normalizedPhoneInputId = `${formId}__otp-normalized-phone`;
    const phoneCountryCodeInputId = `${formId}__otp-phone-country-code`;
    const phoneNumberInputId = `${formId}__otp-phone-number`;
    const countryCodeSelect = new Select(
      phoneCountryCodeInputId,
      COUNTRY_CODE_SELECT_CONFIG,
    );
    this._phoneHandlerCommon({
      phoneFormId,
      countryCodeSelect,
      normalizedPhoneInputId,
      phoneNumberInputId,
      formEl,
      submitBtn,
      phoneElKey: 'phoneNumberEl',
    });
  }

  _checkParams = (conditions = {}) => {
    const params = new URLSearchParams(window.location.search);
    return Object.entries(conditions).every((entry) => {
      const [key, value] = entry;
      return params.get(key) === value;
    });
  };

  _setToastifyClass = (toastType) => {
    let shiftToastifyClass = '';
    if (this._checkParams({ iframe: 'true' })) {
      shiftToastifyClass = 'scaler-event__toastify-shift';
    }

    return `toastify-${toastType} ${shiftToastifyClass}`;
  };

  /* Form handlers */
  _eventsApplicationFormPhoneHandler = (el) => {
    const registerBtn = el.querySelector(`#${formId}-submit`);
    const registrationTracking = new FormTracker(el.id);
    const normalizedPhoneInputId = `${formId}-normalized-phone`;
    const normalizedPhoneEl = document.getElementById(normalizedPhoneInputId);

    function getFieldValue(fieldName) {
      if (document.querySelector(`input[id="input-${formId}-${fieldName}"]`)) {
        return document.querySelector(
          `input[id="input-${formId}-${fieldName}"]`,
        ).value;
      } else if (document.querySelector(`input[id="${formId}-${fieldName}"]`)) {
        return document.querySelector(
          `input[id="${formId}-${fieldName}"]`,
        ).value;
      } else if (document.querySelector(
        `#${formId} input[name="${fieldName}"]`,
      )) {
        return document.querySelector(
          `#${formId} input[name="${fieldName}"]`,
        ).value;
      } else {
        return null;
      }
    }

    const payload = {
      attributions: getAttribution(),
    };

    formFieldNames.forEach(field => {
      if (field) {
        payload[field] = getFieldValue(field);
      }
    });

    const form = new Form(el, {
      method: 'POST',
      endpoint: `/apply/event/${this.eventSlug}`,
      createPayload: ({
        phone_country_code: countryCode,
        normalized_phone: normalizedPhone,
        user_email: userEmail,
        whatsapp_consent: whatsappConsent,
        name,
        university,
        orgyear,
        orgname,
        position,
        other_position: otherPosition,
      }) => ({
        attributions: getAttribution(),
        phone_number: `${countryCode}-${normalizedPhone}`,
        email: userEmail,
        whatsapp_consent: whatsappConsent,
        full_name: name,
        university,
        orgyear,
        orgname,
        position,
        other_position: otherPosition,
      }),
    });

    form.on('initialize', () => {
      setAttribution('event_register_logged_in_modal');
      registerBtn.disabled = false;
      if (whatsappConsentInputEl) {
        whatsappConsentInputEl.addEventListener('click', () => {
          if (whatsappConsentInputEl.value === 'whatsapp_consent_yes') {
            whatsappConsentInputEl.setAttribute('value', 'whatsapp_consent_no');
          } else {
            whatsappConsentInputEl.setAttribute(
              'value', 'whatsapp_consent_yes',
            );
          }
        });
      }

      if (normalizedPhoneEl) {
        this.initializePhoneHandler(el, registerBtn);
      }
      registrationTracking._initializeInputClickTracking();
    });

    form.on('submit', () => {
      form._options.endpoint = `/apply/event/${this.eventSlug}`;

      eventPage.classList.add('clicked');
    });

    form.on('submitted', () => {
      if (otp === true) {
        otpEl.classList.remove('hide');
        this.switchMode(`${formId}-otp-state`);
        Toastify({
          text: 'Successfully register for event, sent OTP for verification',
          className: this._setToastifyClass('success'),
        }).showToast();
        registrationTracking.formStatusTracking(
          `${el.id}-success`,
          {
            success_message:
            'Successfully register for event, sent OTP for verification',
          },
        );
        setAttribution('event_register_form_first_otp');
      } else {
        this.complete();
        Toastify({
          text: 'You are successfully registered for the event',
          className: this._setToastifyClass('success'),
        }).showToast();
        registrationTracking.formStatusTracking(
          `${el.id}-success`,
          { success_message: 'You are successfully registered for the event' },
        );
      }
      eventPage.classList.remove('clicked');
      gtmTracking.sendFormSubmitStatus(el.id, true);
    });

    form.on('error', (error) => {
      eventPage.classList.remove('clicked');
      const message = error?.responseJson?.message || `Something went wrong! 
      Refresh the page and try again`;
      Toastify({
        text: message,
        className: this._setToastifyClass('danger'),
      }).showToast();
      registerBtn.disabled = false;
      registrationTracking.formStatusTracking(
        `${el.id}-error`,
        { error_message: message },
      );
      gtmTracking.sendFormSubmitStatus(el.id, false, message);
    });

    form.initialize();

    return form;
  }

  _eventsApplicationFormOtpStateHandler = (el) => {
    const registrationTracking = new FormTracker(el.id);
    const registerBtn = el.querySelector(`#${formId}-otp-submit`);

    const form = new Form(el, {
      method: 'POST',
      endpoint: `/verify-otp?event_slug=${this.eventSlug}`,
      createPayload: ({
        phone_country_code: countryCode,
        normalized_phone: userNumber,
      }) => ({
        phone_number: `${countryCode}-${userNumber}`,
        otp: document.querySelector(`input[id="${formId}-otp"]`).value,
        email: document.querySelector(
          `input[id="${formId}-email"]`,
        ).value,
        attributions: getAttribution(),
      }),
    });

    form.on('initialize', () => {
      const normalizedPhoneInputId = `${formId}__otp-normalized-phone`;
      const normalizedPhoneEl = document.getElementById(normalizedPhoneInputId);
      normalizedPhoneEl.disabled = true;
      registrationTracking._initializeInputClickTracking();
      this.initializeOtpPhoneHandler(el, registerBtn);
    });

    form.on('submit', () => {
      form._options.endpoint = `/verify-otp?event_slug=${this.eventSlug}`;
      eventPage.classList.add('clicked');
    });

    form.on('submitted', () => {
      this.complete();
      Toastify({
        text: 'Phone number has been verified',
        className: this._setToastifyClass('success'),
      }).showToast();
      registrationTracking.formStatusTracking(
        `${el.id}-success`,
        { success_message: 'Phone number has been verified' },
      );
      eventPage.classList.remove('clicked');
      gtmTracking.sendFormSubmitStatus(el.id, true);
    });

    form.on('error', (error) => {
      eventPage.classList.remove('clicked');
      const message = error?.responseJson?.message || `Something went wrong! 
      Refresh the page and try again`;
      Toastify({
        text: message,
        className: this._setToastifyClass('danger'),
      }).showToast();
      registerBtn.disabled = false;
      registrationTracking.formStatusTracking(
        `${el.id}-error`,
        { error_message: message },
      );
      gtmTracking.sendFormSubmitStatus(el.id, false, message);
    });

    form.initialize();
    return form;
  }
}

export default ApplicationForm;
